import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { articlesData } from "@/mocks/articlesData";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  const article = articlesData.find((a) => a.slug === slug);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-24 pb-20 px-4 md:px-6 bg-background-50 min-h-screen">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
              Article Not Found
            </h1>
            <p className="text-foreground-600 mb-8">
              Sorry, the article you are looking for does not exist or has been moved.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              Back to Home
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.excerpt,
    "image": article.heroImage,
    "datePublished": "2026-06-25",
    "dateModified": "2026-06-25",
    "author": {
      "@type": "Organization",
      "name": "AcBuySpreadsheet Buying Research Team"
    },
    "publisher": {
      "@type": "Organization",
      "name": "AcBuySpreadsheet",
      "url": "https://acbuyspreadsheets.buzz/",
      "logo": {
        "@type": "ImageObject",
        "url": "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/acdcfd47829ab306d380ea84eb14e50b.jpeg"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://acbuyspreadsheets.buzz/article/" + article.slug
    }
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://acbuyspreadsheets.buzz/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": article.title,
        "item": "https://acbuyspreadsheets.buzz/article/" + article.slug
      }
    ]
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        <article>
          <section className="py-16 md:py-24 px-4 md:px-6 bg-background-50">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                <div className="w-full lg:w-5/12">
                  <span className="inline-block text-xs font-medium text-foreground-500 tracking-widest uppercase mb-4">
                    Ultimate Guide · {article.date}
                  </span>
                  <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-900 leading-tight mb-6">
                    {article.title}
                  </h1>
                  <p className="text-lg text-foreground-600 leading-relaxed">
                    {article.excerpt}
                  </p>
                </div>
                <div className="w-full lg:w-7/12">
                  <div className="rounded-2xl overflow-hidden h-64 md:h-80 lg:h-[420px]">
                    <img
                      src={article.heroImage}
                      alt={article.title}
                      title={article.title}
                      className="w-full h-full object-cover"
                      loading="eager"
                    />
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
            <div className="max-w-3xl mx-auto">
              <div className="prose-custom">
                <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
                  {article.introduction}
                </p>

                {article.sections.map((section) => (
                  <div key={section.heading}>
                    <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5 mt-12">
                      {section.heading}
                    </h2>
                    {section.paragraphs.map((para, pIdx) => (
                      <p
                        key={pIdx}
                        className="text-base md:text-lg text-foreground-600 leading-relaxed mb-6"
                      >
                        {para}
                      </p>
                    ))}
                  </div>
                ))}

                <div className="mt-12 p-6 md:p-8 bg-accent-50 rounded-2xl border border-accent-100">
                  <h3 className="text-xl font-heading font-bold text-foreground-900 mb-3">
                    Ready to Start Shopping?
                  </h3>
                  <p className="text-base text-foreground-600 leading-relaxed mb-5">
                    The <strong>best acbuy spreadsheet 2026</strong> is waiting for you. Browse our curated categories, find trusted sellers, and discover your next favorite fashion piece today. All links are verified and updated weekly.
                  </p>
                  <a
                    href="https://kaspreadsheet.com/"
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-full transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {article.ctaText}
                    <i className="ri-arrow-right-line text-lg"></i>
                  </a>
                </div>
              </div>
            </div>
          </section>

          <section className="py-16 md:py-20 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-5 gap-4 h-[300px] md:h-[400px]">
                <div className="col-span-2 rounded-2xl overflow-hidden">
                  <img
                    src={article.articleImages[0]}
                    alt={article.title + " illustration"}
                    title={article.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <div className="col-span-3 rounded-2xl overflow-hidden">
                  <img
                    src={article.articleImages[1]}
                    alt={article.title + " overview"}
                    title={article.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </section>
        </article>

        <section className="py-16 md:py-20 px-4 md:px-6 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-8 text-center">
              More{" "}
              <span className="text-accent-500">Buying Guides</span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
              {articlesData
                .filter((a) => a.slug !== slug)
                .slice(0, 6)
                .map((a) => (
                  <Link
                    key={a.slug}
                    to={"/article/" + a.slug}
                    className="group p-5 rounded-xl bg-white border border-background-200/70 hover:border-accent-200 transition-colors cursor-pointer"
                  >
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-accent-600 transition-colors mb-2 line-clamp-2">
                      {a.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{a.excerpt}</p>
                  </Link>
                ))}
            </div>
          </div>
        </section>

        <section className="py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto text-center">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-base font-medium text-foreground-600 hover:text-accent-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Back to AcBuySpreadsheet Home
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(breadcrumbSchema),
        }}
      />
    </>
  );
}